// Source of truth for design tokens consumed from TypeScript (Antd theme,
// styled-components, inline styles, JS-driven CSS variables).
//
// KEEP IN SYNC with `src/assets/less/variables/_variables.less`.
// LESS files import the @-prefixed names from `_variables.less` directly;
// TS callers import from here. Both must point at the same hex / numeric values.

export const colors = {
  // Neutral
  neutral0:    '#ffffff',
  neutral50:   '#edeff5',
  neutral100:  '#dce0e9',
  neutral150:  '#ccd2de',
  neutral200:  '#bcc4d3',
  neutral300:  '#9fa9bd',
  neutral400:  '#8691a7',
  neutral500:  '#707b91',
  neutral600:  '#6c778c',
  neutral700:  '#4a5365',
  neutral800:  '#394050',
  neutral900:  '#292e3a',
  neutral1000: '#191d24',

  // Primary — anchored to #0769D9 per .figma/guidelines.md § Tokens — Color
  primary:           '#0769d9',
  primaryHover:      '#258bf6',
  primaryPressed:    '#0050b4',
  primaryBgSubtle:   '#ecf8ff',
  primaryBgStrong:   '#d6f0ff',
  brandAccent:       '#9747ff', // docs/marketing only

  // Status
  success:        '#65c376',
  successBg:      '#f6ffed',
  successBorder:  '#b7eb8f',
  warning:        '#f99e33',
  warningBg:      '#fff7e6',
  warningBorder:  '#ffd591',
  error:          '#ff4d4f',
  errorHover:     '#ff7875',
  errorBg:        '#fff2f0',
  errorBorder:    '#ffa39e',
  info:           '#258bf6',
  infoBg:         '#ecf8ff',
  infoBorder:     '#91caff',

  // Surfaces
  bgPage:          '#fbfbfb',
  bgCanvas:        '#ffffff',
  bgSubtleSurface: '#f3f4f8',
  borderDefault:   '#edeff5',
  borderStrong:    '#dce0e9',

  // Text
  textPrimary:    '#394050',
  textSecondary:  '#707b91',
  textTertiary:   '#8691a7',
  textQuaternary: '#9fa9bd',
  textInverse:    '#ffffff',
} as const

export const radius = {
  none: 0,
  xs:   2,
  sm:   4,
  base: 6,    // Figma canon + Antd 5/6 default
  lg:   8,
  xl:   12,
  xxl:  16,
  pill: 9999,
} as const

export const spacing = {
  x0:  0,
  x2:  2,
  x4:  4,
  x6:  6,
  x8:  8,
  x12: 12,
  x16: 16,
  x20: 20,
  x24: 24,
  x32: 32,
  x40: 40,
  x48: 48,
  x56: 56,
  x64: 64,
  x80: 80,
} as const

export const typography = {
  fontFamilyDisplay: '"Inter", "PT Sans", sans-serif',
  fontFamilyBody:    '"PT Sans", sans-serif',
  fontFamilyMono:    '"Space Mono", ui-monospace, monospace',

  fontSizeBase:        14,
  fontSizeH1:          '2rem',
  fontSizeH2:          '1.5rem',
  fontSizeH3:          '1.25rem',
  fontSizeH4:          '1rem',
  fontSizeH5:          '0.875rem',
  fontSizeH6:          '0.75rem',
  fontSizeBodyLarge:   '1rem',
  fontSizeBodyMedium:  '0.875rem',
  fontSizeBodySmall:   '0.75rem',

  weightLight:      300,
  weightRegular:    400,
  weightMedium:     500,
  weightSemiBold:   600,
  weightBold:       700,
  weightExtraBold:  800,
  weightBlack:      900,
} as const

export const motion = {
  durFast: '150ms',
  durMed:  '200ms',
  ease:    'cubic-bezier(0.215, 0.61, 0.355, 1)',
} as const

export const layout = {
  pageMaxWidth:      1200,
  pageGutter:        120,
  sectionGapDefault: 48,
  sectionGapLoose:   80,
  siderCollapsed:    60,
  siderExpanded:     200,
  headerHeight:      64,
  formGapField:      16,
  formGapGroup:      24,
  cardPaddingCompact: 16,
  cardPaddingDefault: 24,
  drawerWidthWide:   600,
} as const

export const elevation = {
  cardSubtle:       '0 2px 4px 0 rgba(0,0,0,0.04), 0 1px 6px -1px rgba(0,0,0,0.02), 0 1px 2px 0 rgba(0,0,0,0.04)',
  cardElevated:     '0 9px 28px 8px rgba(0,0,0,0.05), 0 3px 6px -4px rgba(0,0,0,0.12), 0 6px 16px 0 rgba(0,0,0,0.08)',
  focusRing:        '0 0 0 2px rgba(5, 145, 255, 0.10)',
  focusRingError:   '0 0 0 2px rgba(255, 38, 6, 0.10)',
  focusRingWarning: '0 0 0 2px rgba(255, 215, 5, 0.10)',
} as const

export const surfaces = {
  modalMaskBg: 'rgba(0, 0, 0, 0.45)',
} as const

export const tokens = {
  colors,
  radius,
  spacing,
  typography,
  motion,
  layout,
  elevation,
  surfaces,
} as const

export type GiroTokens = typeof tokens
